/**
 * Login / Signup — aligned to Landing v2 aesthetic.
 *
 * Layout: split hero (left: navy brand + value prop, right: ivory card with
 * form + gold CTA). Display font Cabinet Grotesk for headings. No dark-mode
 * bleed — forces `data-theme="light"` while mounted.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  ArrowRight, Loader2, Lock, Mail, User, Building2, Eye, EyeOff, Sparkles,
  Check, X as XIcon, Zap, Star,
} from "lucide-react";
import { useAuth, formatApiErrorDetail } from "../auth/AuthContext";
import LangFlags from "../components/LangFlags";
import SSOAccessPanel from "../components/SSOAccessPanel";
import InstallPromptButton from "../components/InstallPromptButton";
import { useLang } from "../i18n/LangContext";

// Inline login-page copy keyed by locale so the 3-flag switcher has visible
// effect even though our global `translations.js` bag is EN/PT only for now.
const LOGIN_COPY = {
  pt: {
    hero_eyebrow: "14 dias grátis · sem cartão",
    hero_title_login: "Bem-vindo de volta.",
    hero_title_signup: "Feche mais,",
    hero_accent_login: "Abra seu pipeline.",
    hero_accent_signup: "com inteligência.",
    hero_body_login: "Retome de onde parou. Seus leads, auditorias e cadências a um clique.",
    hero_body_signup: "Encontre empresas reais no Google Maps, qualifique com IA, dispare cadências do Kanban — tudo num só lugar.",
    bullets: ["Google Maps Places · APIs oficiais", "Auditorias GPT em <90s", "CRM Kanban · Biblioteca de Sequências", "Prospecção Social nível Apify"],
    testimonial: "A auditoria virou nosso gancho de abertura.",
    chip_signin: "Entrar",
    chip_signup: "Criar conta",
    form_title_login: "Bem-vindo de volta",
    form_title_signup: "Crie sua conta",
    form_sub_login: "Entre para acessar seu pipeline.",
    form_sub_signup: "Acesse a plataforma e comece a gerar leads em minutos.",
    ph_name: "Seu nome", ph_company: "Empresa", ph_email: "E-mail", ph_password: "Senha",
    btn_login: "Entrar", btn_signup: "Criar conta · GRÁTIS",
    tip_pw: "💡 Senha tem {n} caracteres — a master tem 20.",
    switch_login: "Já tem conta?", switch_signup: "Ainda não tem conta?",
    switch_link_login: "Entrar", switch_link_signup: "Criar conta",
    back_to_site: "← Voltar para o site",
  },
  en: {
    hero_eyebrow: "14-day FREE TRIAL · no credit card",
    hero_title_login: "Welcome back.",
    hero_title_signup: "Start closing",
    hero_accent_login: "Open the pipeline.",
    hero_accent_signup: "smarter.",
    hero_body_login: "Pick up where you left off. Your leads, audits and sequences are one click away.",
    hero_body_signup: "Find real businesses on Google Maps, score them with AI, run cadences from Kanban — all inside one cockpit.",
    bullets: ["Google Maps Places · official APIs", "GPT audits in <90s", "Kanban CRM · Sequence Library", "Apify-grade Social Prospecting"],
    testimonial: "The audit became our opening line.",
    chip_signin: "Sign in", chip_signup: "Create account",
    form_title_login: "Welcome back", form_title_signup: "Create your account",
    form_sub_login: "Sign in to access your pipeline.",
    form_sub_signup: "Get on the platform and start generating leads in minutes.",
    ph_name: "Your name", ph_company: "Company", ph_email: "Email", ph_password: "Password",
    btn_login: "Sign in", btn_signup: "Create account · FREE TRIAL",
    tip_pw: "💡 Password is {n} chars — master has 20.",
    switch_login: "Already have an account?", switch_signup: "Don't have an account yet?",
    switch_link_login: "Sign in", switch_link_signup: "Create account",
    back_to_site: "← Back to site",
  },
  es: {
    hero_eyebrow: "14 días gratis · sin tarjeta",
    hero_title_login: "Bienvenido de nuevo.",
    hero_title_signup: "Cierra más,",
    hero_accent_login: "Abre el pipeline.",
    hero_accent_signup: "con inteligencia.",
    hero_body_login: "Retoma donde lo dejaste. Tus leads, auditorías y secuencias a un clic.",
    hero_body_signup: "Encuentra empresas reales en Google Maps, califícalas con IA y lanza cadencias desde el Kanban — todo en un solo lugar.",
    bullets: ["Google Maps Places · APIs oficiales", "Auditorías GPT en <90s", "CRM Kanban · Biblioteca de Secuencias", "Prospección Social nivel Apify"],
    testimonial: "La auditoría se volvió nuestra frase de apertura.",
    chip_signin: "Acceder", chip_signup: "Crear cuenta",
    form_title_login: "Bienvenido de nuevo", form_title_signup: "Crea tu cuenta",
    form_sub_login: "Accede para entrar a tu pipeline.",
    form_sub_signup: "Entra a la plataforma y empieza a generar leads en minutos.",
    ph_name: "Tu nombre", ph_company: "Empresa", ph_email: "Correo electrónico", ph_password: "Contraseña",
    btn_login: "Acceder", btn_signup: "Crear cuenta · GRATIS",
    tip_pw: "💡 La contraseña tiene {n} caracteres — la master tiene 20.",
    switch_login: "¿Ya tienes cuenta?", switch_signup: "¿Aún no tienes cuenta?",
    switch_link_login: "Acceder", switch_link_signup: "Crear cuenta",
    back_to_site: "← Volver al sitio",
  },
};

const LOGO_URL = "/logos/agentelead-pro-dark-bg.png";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, submit2faChallenge } = useAuth();
  const { lang } = useLang();
  const c = LOGIN_COPY[lang] || LOGIN_COPY.en;

  const initialMode = location.pathname === "/signup" ? "signup" : "login";
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // 2FA challenge state — populated after a successful password but before
  // the access token is granted. UI swaps to the 6-digit input.
  const [twoFA, setTwoFA] = useState(null);  // { challenge_token, partial }
  const [twoFAcode, setTwoFAcode] = useState("");

  const isSignup = mode === "signup";

  useEffect(() => {
    const root = document.documentElement;
    const prev = root.getAttribute("data-theme");
    root.setAttribute("data-theme", "light");
    return () => { if (prev !== null) root.setAttribute("data-theme", prev); else root.removeAttribute("data-theme"); };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(""); setSubmitting(true);
    try {
      let logged;
      if (isSignup) {
        logged = await register({ email: email.trim(), password, name: name.trim(), company: company.trim() });
      } else {
        logged = await login(email.trim(), password);
      }
      // 2FA gate — pause here, show second-factor input.
      if (logged?.requires_2fa) {
        setTwoFA({ challenge_token: logged.challenge_token, partial: logged.partial });
        setSubmitting(false);
        return;
      }
      // Iter48 — branch based on role + must_change_password.
      let next = new URLSearchParams(location.search).get("next") || null;
      if (!next) {
        if (logged?.must_change_password) next = "/change-password";
        else if (logged?.role === "super_admin" || logged?.role === "admin" || logged?.role === "master_demo") next = "/master";
        else next = "/dashboard";
      }
      window.location.assign(next);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err?.message || "Failed");
    } finally { setSubmitting(false); }
  };

  const handle2FA = async (e) => {
    e.preventDefault(); setError(""); setSubmitting(true);
    try {
      const me = await submit2faChallenge(twoFA.challenge_token, twoFAcode);
      let next = new URLSearchParams(location.search).get("next") || null;
      if (!next) {
        if (me?.must_change_password) next = "/change-password";
        else if (me?.role === "super_admin" || me?.role === "admin" || me?.role === "master_demo") next = "/master";
        else next = "/dashboard";
      }
      window.location.assign(next);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err?.message || "Invalid 2FA code");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="login-page"
      className="min-h-screen font-body antialiased grid grid-cols-1 lg:grid-cols-12"
      style={{ background: "#FBF9F4", color: "#0F172A" }}>
      {/* Left hero (hidden on mobile) */}
      <aside className="hidden lg:flex lg:col-span-5 xl:col-span-6 relative overflow-hidden text-white flex-col justify-between p-10"
        style={{ background: "#0A1233" }}>
        <div className="pointer-events-none absolute inset-0" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.06) 1px, transparent 0)", backgroundSize: "32px 32px" }} />
        <div className="pointer-events-none absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full blur-3xl" style={{ backgroundColor: "rgba(201,162,78,0.15)" }} />
        <div className="relative z-10">
          <Link to="/landing" className="flex items-center gap-2.5" data-testid="login-brand">
            <img src={LOGO_URL} alt="AgenteLead Pro" className="h-12 w-12 object-contain" />
            <div>
              <div className="font-display text-[22px] font-extrabold tracking-tight leading-none">AgenteLead<span style={{ color: "#C9A24E" }}>.Pro</span></div>
              <div className="text-[10.5px] tracking-[0.22em] font-bold mt-1" style={{ color: "rgba(229,197,114,0.85)" }}>FIND. ENGAGE. GROW.</div>
            </div>
          </Link>
          <div className="mt-16">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-[10.5px] font-bold uppercase tracking-[0.18em]" style={{ borderColor: "rgba(201,162,78,0.45)", backgroundColor: "rgba(201,162,78,0.12)", color: "#E5C572" }}>
              <Zap className="w-3 h-3" /> {c.hero_eyebrow}
            </span>
            <h1 className="font-display mt-6 text-5xl lg:text-6xl font-black tracking-tighter leading-[0.98]">
              {isSignup ? c.hero_title_signup : c.hero_title_login}<br />
              <span style={{ color: "#C9A24E" }}>{isSignup ? c.hero_accent_signup : c.hero_accent_login}</span>
            </h1>
            <p className="mt-5 text-[15px] leading-relaxed max-w-md" style={{ color: "rgba(255,255,255,0.72)" }}>
              {isSignup ? c.hero_body_signup : c.hero_body_login}
            </p>
            <ul className="mt-8 space-y-2.5 text-[13.5px]" style={{ color: "rgba(255,255,255,0.86)" }}>
              {c.bullets.map((b) => (
                <li key={b} className="flex items-center gap-2"><Check className="w-4 h-4 shrink-0" style={{ color: "#10B981" }} />{b}</li>
              ))}
            </ul>
          </div>
        </div>
        <div className="relative z-10 flex items-center gap-4">
          <div className="flex -space-x-2">
            {["https://images.unsplash.com/photo-1576558656222-ba66febe3dec?w=80&q=70","https://images.unsplash.com/photo-1655249481446-25d575f1c054?w=80&q=70"].map((src) => (
              <img key={src} src={src} alt="" className="w-9 h-9 rounded-full object-cover ring-2" style={{ ["--tw-ring-color"]: "#0A1233" }} />
            ))}
          </div>
          <div>
            <div className="flex gap-0.5 mb-0.5">{[1,2,3,4,5].map((n) => <Star key={n} className="w-3 h-3" fill="#C9A24E" style={{ color: "#C9A24E" }} />)}</div>
            <div className="text-[12px]" style={{ color: "rgba(255,255,255,0.72)" }}>"{c.testimonial}" · <span className="font-semibold">B2B Consultancy</span></div>
          </div>
        </div>
      </aside>

      {/* Right panel (form) */}
      <main className="lg:col-span-7 xl:col-span-6 flex items-center justify-center p-6 lg:p-12 relative">
        {/* Iter266 · Move LangFlags out of the absolute top-right on mobile
             so they don't overlap the InstallPromptButton in the brand row. */}
        <LangFlags className="hidden lg:block absolute top-5 right-5 z-20" />
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center justify-between gap-2 mb-6">
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="" className="h-10 w-10 object-contain" />
              <span className="font-display text-[18px] font-extrabold tracking-tight" style={{ color: "#0A1233" }}>AgenteLead<span style={{ color: "#C9A24E" }}>.Pro</span></span>
            </div>
            <div className="flex items-center gap-2 relative z-30">
              <LangFlags className="lg:hidden" />
              <InstallPromptButton variant="chip" />
            </div>
          </div>

          <div className="rounded-2xl p-7 lg:p-9 shadow-xl border" style={{ backgroundColor: "#FFFFFF", borderColor: "#E5E2DA" }}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border text-[10.5px] font-bold uppercase tracking-[0.18em]" style={{ borderColor: "#E5E2DA", backgroundColor: "#F5F2EA", color: "#475569" }}>
              <Sparkles className="w-3 h-3" style={{ color: "#C9A24E" }} />
              {isSignup ? c.chip_signup : c.chip_signin}
            </div>
            <h2 className="font-display mt-4 text-3xl lg:text-4xl font-black tracking-tight" style={{ color: "#0A1233" }}>
              {isSignup ? c.form_title_signup : c.form_title_login}
            </h2>
            <p className="text-[14px] mt-1.5" style={{ color: "#64748B" }}>
              {isSignup ? c.form_sub_signup : c.form_sub_login}
            </p>

            <form onSubmit={twoFA ? handle2FA : handleSubmit} className="mt-6 space-y-3" data-testid="login-form" autoComplete="off">
              {twoFA ? (
                <div data-testid="login-2fa-step" className="space-y-3">
                  <div className="px-3.5 py-3 rounded-lg border-2 border-amber-200 bg-amber-50 text-[12.5px] text-amber-900 leading-relaxed">
                    <div className="font-bold mb-1">{lang === "pt" ? "Autenticação de dois fatores" : lang === "es" ? "Autenticación de dos factores" : "Two-factor authentication"}</div>
                    {lang === "pt"
                      ? "Digite o código de 6 dígitos do seu app autenticador (ou um código de recuperação)."
                      : lang === "es"
                      ? "Introduce el código de 6 dígitos de tu app autenticadora (o un código de recuperación)."
                      : "Enter the 6-digit code from your authenticator app (or a recovery code)."}
                  </div>
                  <input
                    type="text" inputMode="text" autoFocus
                    value={twoFAcode}
                    onChange={(e) => setTwoFAcode(e.target.value.trim())}
                    placeholder="000000"
                    data-testid="login-2fa-code"
                    className="w-full h-12 rounded-lg border-2 border-[#E5E7EB] focus:border-[#C9A24E] outline-none text-center font-mono-tech text-[20px] tracking-[0.4em]"
                  />
                  <button type="button" onClick={() => { setTwoFA(null); setTwoFAcode(""); setError(""); }}
                    className="text-[12.5px] text-[#64748B] hover:underline">
                    {lang === "pt" ? "← Voltar para login" : lang === "es" ? "← Volver al login" : "← Back to login"}
                  </button>
                </div>
              ) : (<>
              {!isSignup && <SSOAccessPanel lang={lang} />}
              {isSignup && (
                <>
                  <Field icon={User} placeholder={c.ph_name} value={name} onChange={setName} testid="login-name" required />
                  <Field icon={Building2} placeholder={c.ph_company} value={company} onChange={setCompany} testid="login-company" />
                </>
              )}
              <Field icon={Mail} type="email" placeholder={c.ph_email} value={email} onChange={setEmail} testid="login-email" required />
              <div className="relative">
                <Field icon={Lock} type={showPw ? "text" : "password"} placeholder={c.ph_password}
                  value={password} onChange={setPassword} testid="login-password" required minLength={6}
                  autoComplete={isSignup ? "new-password" : "off"} />
                <button type="button" onClick={() => setShowPw((s) => !s)}
                  data-testid="login-toggle-password"
                  className="absolute right-3 top-1/2 -translate-y-1/2 hover:text-slate-900" style={{ color: "#94A3B8" }}>
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              {isSignup && password.length > 0 && (
                <PasswordChecklist password={password} lang={lang} />
              )}
              </>)}

              {error && (
                <div data-testid="login-error" className="text-[13px] rounded-lg px-3 py-2 border" style={{ backgroundColor: "#FEF2F2", borderColor: "#FECACA", color: "#B91C1C" }}>
                  {error}
                  {!isSignup && password && password.length !== 20 && error.toLowerCase().includes("invalid") && (
                    <div className="mt-1 text-[11.5px]" style={{ color: "#92400E" }}>
                      {c.tip_pw.replace("{n}", password.length)}
                    </div>
                  )}
                </div>
              )}

              <button type="submit" disabled={submitting || (twoFA && twoFAcode.length < 6)} data-testid="login-submit"
                className="w-full inline-flex items-center justify-center gap-2 font-bold text-[14px] py-3.5 rounded-xl transition-all hover:-translate-y-0.5 disabled:opacity-60"
                style={{ backgroundColor: "#C9A24E", color: "#0A1233", boxShadow: "0 8px 30px -10px rgba(201,162,78,0.55)" }}>
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {twoFA ? (lang === "pt" ? "Verificar código" : lang === "es" ? "Verificar código" : "Verify code") : (isSignup ? c.btn_signup : c.btn_login)}
                {!submitting && <ArrowRight className="w-4 h-4" />}
              </button>
            </form>

            {/* Iter120 — Sign in with Google (Emergent-managed). Hidden
                during the 2FA challenge because that flow is mid-JWT. */}
            {!twoFA && (
              <>
                <div className="flex items-center gap-2 my-4">
                  <div className="flex-1 h-px" style={{ backgroundColor: "#E5E2DA" }} />
                  <span className="text-[11px] uppercase tracking-wide" style={{ color: "#94A3B8" }}>
                    {lang === "pt" ? "ou" : lang === "es" ? "o" : "or"}
                  </span>
                  <div className="flex-1 h-px" style={{ backgroundColor: "#E5E2DA" }} />
                </div>
                <button
                  type="button"
                  data-testid="btn-google-signin"
                  onClick={() => {
                    const redirect = `${window.location.origin}/auth/callback`;
                    window.location.href =
                      `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
                  }}
                  className="w-full inline-flex items-center justify-center gap-3 font-semibold text-[14px] py-3 rounded-xl border transition-colors hover:bg-slate-50"
                  style={{ borderColor: "#E5E2DA", backgroundColor: "#FFFFFF", color: "#0F172A" }}
                >
                  <GoogleGIcon />
                  {isSignup
                    ? (lang === "pt" ? "Cadastrar com Google" : lang === "es" ? "Registrarse con Google" : "Sign up with Google")
                    : (lang === "pt" ? "Entrar com Google" : lang === "es" ? "Iniciar sesión con Google" : "Sign in with Google")}
                </button>
              </>
            )}

            <div className="mt-5 text-center text-[13px]" style={{ color: "#475569" }}>
              {isSignup ? (
                <>{c.switch_login}{" "}<button type="button" onClick={() => { setMode("login"); setError(""); navigate("/login"); }} className="font-semibold hover:underline" style={{ color: "#C9A24E" }} data-testid="login-switch-to-login">{c.switch_link_login}</button></>
              ) : (
                <>{c.switch_signup}{" "}<button type="button" onClick={() => { setMode("signup"); setError(""); navigate("/signup"); }} className="font-semibold hover:underline" style={{ color: "#C9A24E" }} data-testid="login-switch-to-signup">{c.switch_link_signup}</button></>
              )}
            </div>
          </div>

          <div className="text-center mt-6 text-[12px]" style={{ color: "#94A3B8" }}>
            <Link to="/landing" className="hover:text-slate-700" data-testid="login-back-landing">{c.back_to_site}</Link>
          </div>
        </div>
      </main>
    </div>
  );
}

function Field({ icon: Icon, testid, type = "text", placeholder, value, onChange, required, minLength, autoComplete }) {
  return (
    <div className="relative">
      <Icon className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "#94A3B8" }} />
      <input
        type={type} required={required} minLength={minLength} placeholder={placeholder}
        value={value} onChange={(e) => onChange(e.target.value)} autoComplete={autoComplete}
        data-testid={testid}
        className="w-full rounded-xl pl-10 pr-4 py-3 text-[14px] outline-none transition-colors"
        style={{ border: "1px solid #E5E2DA", backgroundColor: "#FFFFFF", color: "#0F172A" }} />
    </div>
  );
}

function GoogleGIcon() {
  // Kept for backwards-compat with any other imports — no longer rendered on
  // the Login page (the SSO Access Panel now owns the Google CTA).
  return (
    <svg viewBox="0 0 48 48" className="w-4 h-4" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 18.9 12 24 12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.3 0 10.1-2 13.7-5.3l-6.3-5.3c-2 1.4-4.6 2.3-7.4 2.3-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.6 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.4l6.3 5.3C40.9 35.5 44 30.2 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  );
}
// Suppress "unused" warning — kept as a named export for future reuse.
// eslint-disable-next-line no-unused-vars
const _GoogleGIconRef = GoogleGIcon;

function PasswordChecklist({ password, lang }) {
  const labels = {
    pt: { len: "Pelo menos 8 caracteres", upper: "Letra maiúscula (A-Z)", num: "Número (0-9)", sym: "Símbolo (!@#$…)" },
    en: { len: "At least 8 characters", upper: "Uppercase letter (A-Z)", num: "Number (0-9)", sym: "Symbol (!@#$…)" },
    es: { len: "Al menos 8 caracteres", upper: "Mayúscula (A-Z)", num: "Número (0-9)", sym: "Símbolo (!@#$…)" },
  };
  const L = labels[lang] || labels.en;
  const checks = [
    { key: "len",   ok: password.length >= 8,            label: L.len },
    { key: "upper", ok: /[A-Z]/.test(password),          label: L.upper },
    { key: "num",   ok: /\d/.test(password),             label: L.num },
    { key: "sym",   ok: /[^A-Za-z0-9]/.test(password),   label: L.sym },
  ];
  return (
    <ul data-testid="signup-password-checklist" className="grid grid-cols-2 gap-x-3 gap-y-1 text-[12px] mt-1">
      {checks.map((c) => (
        <li key={c.key} data-testid={`signup-pwcheck-${c.key}`} className="flex items-center gap-1.5"
          style={{ color: c.ok ? "#059669" : "#94A3B8" }}>
          {c.ok ? <Check className="w-3.5 h-3.5" strokeWidth={3} /> : <XIcon className="w-3.5 h-3.5" />}
          <span className={c.ok ? "font-semibold" : ""}>{c.label}</span>
        </li>
      ))}
    </ul>
  );
}
